import moment from "moment";
import React, { useEffect, useState } from "react";
import useApis from "../services/useAPI";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faTimes } from "@fortawesome/free-solid-svg-icons";

import Glucose from "../assets/icons/Tests/DNA_Glucose_Management.svg";
import Heart from "../assets/icons/Tests/DNA_Heart_Profile.svg";
import Intolerances from "../assets/icons/Tests/DNA_Intolerances.svg";
import Vitamins from "../assets/icons/Tests/DNA_Vitamins.svg";
import Weight from "../assets/icons/Tests/DNA_Weight_Management.svg";

// Blood test icons
import Cholesterol from "../assets/icons/Tests/Blood_Cholesterol.svg";
import Energy_Profile from "../assets/icons/Tests/Blood_Energy_Profile.svg";
import Female_Sexual_Health from "../assets/icons/Tests/Blood_Female_Sexual_Health.svg";
import General_Health_Test from "../assets/icons/Tests/Blood_General_Health_Test.svg";
import Heart_Profile from "../assets/icons/Tests/Blood_Heart_Profile.svg";
import Male_Sexual_Health from "../assets/icons/Tests/Blood_Male_Sexual_Health.svg";
import Menopause from "../assets/icons/Tests/Blood_Menopause.svg";
import PCOS from "../assets/icons/Tests/Blood_PCOS.svg";
import Thyroid from "../assets/icons/Tests/Blood_Thyroid.svg";
import Erectile_Dysfunction from "../assets/icons/Tests/Blood_Erectile.svg";
import Vitamins_and_Minerals from "../assets/icons/Tests/Blood_Vitamins_and_Minerals.svg";
import Weight_Management from "../assets/icons/Tests/Blood_Weight_Management.svg";
import blood_vitamin_b12 from "../assets/icons/Tests/Blood_vitamin_b12.svg";
import blood_testosterone from "../assets/icons/Tests/Blood_testosterone.svg";

// Biomarkers icons
import Biomarker_Cholesterol from "../assets/icons/Biomarkers/Cholesterol.svg";
import Biomarker_Full_Blood_Count from "../assets/icons/Biomarkers/Full_Blood_Count.svg";
import Biomarker_Hormones from "../assets/icons/Biomarkers/Hormones.svg";
import Biomarker_Kidney from "../assets/icons/Biomarkers/Kidney.svg";
import Biomarker_Liver from "../assets/icons/Biomarkers/Liver.svg";
import Biomarker_Sexual_Health from "../assets/icons/Biomarkers/Sexual_Health.svg";
import Biomarker_Thyroid from "../assets/icons/Biomarkers/Thyroid.svg";
import Biomarker_Vitamins_and_Minerals from "../assets/icons/Biomarkers/Vitamins_and_Minerals.svg";
import { useAuth } from "../components/AuthContext/AuthContext";
import { isSafeUrl } from "../utils";
import PropTypes from "prop-types";
import DOMPurify from "dompurify";
import {
  activationSteps,
  activationStepsSecondary,
  activationStepsSH,
  phlebotomistSteps,
} from "./constants";

const isSexualHealth = (testData, testType, currentPath) =>
  testData?.secondary_barcode_type === "urine" ||
  testType?.includes("sex") ||
  testType?.includes("sexual") ||
  currentPath?.includes("sex") ||
  currentPath?.includes("sexual-health");

const isSecondaryFlow = (testData) =>
  testData?.secondary_barcode_type === "lavender" ||
  testData?.consent_type === "gold_and_lavender" ||
  testData?.secondary_barcode;

const isEmptyBarcodeData = (testData) =>
  !testData?.primary_barcode_check &&
  !testData?.secondary_barcode &&
  !testData?.secondary_barcode_type &&
  !testData?.primary_barcode;

const isPhlebotomyFlow = (testType, currentPath) =>
  currentPath?.includes("/bloods/boots/") || testType?.includes("phlebotomy");

const getStepConfig = (testData, testType, currentPath) => {
  if (isSexualHealth(testData, testType, currentPath)) {
    return activationStepsSH;
  }

  if (isSecondaryFlow(testData)) {
    return isEmptyBarcodeData(testData)
      ? activationSteps
      : activationStepsSecondary;
  }

  if (isPhlebotomyFlow(testType, currentPath)) {
    return phlebotomistSteps;
  }

  return activationSteps;
};

const getAssessmentSubSteps = (testType) => {
  if (!testType) return 3;

  if (testType.match(/sex|sexual|menopause|polycystic/)) return 4;
  if (testType.match(/dna/i)) return 1;

  return 3;
};

const getCurrentStep = (stepConfig, currentPath) => {
  const matchedKey = Object.keys(stepConfig).find((key) =>
    currentPath?.includes(key),
  );

  let currentStep = stepConfig[matchedKey] || 1;

  if (!currentPath?.includes("/assessment/")) return currentStep;

  const parts = currentPath.split("/");
  const index = parts.indexOf("assessment");

  if (index === -1) return currentStep;

  const offset = parts.includes("boots") ? 3 : 2;
  const subStep = Number.parseInt(parts[index + offset], 10);

  if (!Number.isNaN(subStep) && subStep > 0) {
    currentStep += subStep - 1;
  }

  return currentStep;
};

const getStepConfiguration = (testData, testType, currentPath) => {
  const stepConfig = getStepConfig(testData, testType, currentPath);
  const isPhlebotomy = isPhlebotomyFlow(testType, currentPath);

  const baseSteps = Object.keys(stepConfig).length;
  const assessmentSubSteps = getAssessmentSubSteps(testType);

  const totalSteps = isPhlebotomy ? 5 : baseSteps + assessmentSubSteps - 1;

  const currentStep = getCurrentStep(stepConfig, currentPath);

  return {
    stepConfig,
    totalSteps,
    currentStep,
  };
};

/**
 * Calculate progress bar steps
 */
export const calculateProgressSteps = (testData, testType, currentPath) => {
  const { totalSteps, currentStep } = getStepConfiguration(
    testData,
    testType,
    currentPath,
  );
  const completedSteps = currentStep - 1;

  return {
    totalSteps,
    currentStep,
    completedSteps,
  };
};

/**
 * Update progress steps after successful completion
 */
export const updateProgressAfterCompletion = (
  testData,
  testType,
  currentPath,
) => {
  const { totalSteps, currentStep } = getStepConfiguration(
    testData,
    testType,
    currentPath,
  );
  const completedSteps = currentStep;

  return {
    totalSteps,
    currentStep,
    completedSteps,
  };
};

export const formatReportedDate = (dateString) => {
  if (dateString === " ") {
    return " ";
  }
  return new Date(dateString).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

export const formatDateToOrdinal = (dateString) => {
  if (!dateString || dateString === " ") {
    return " ";
  }

  const date = new Date(dateString);

  // Check if date is valid
  if (isNaN(date.getTime())) {
    return " ";
  }

  const day = date.getDate();
  const month = date.toLocaleString("en-GB", { month: "short" });
  const year = date.getFullYear();

  // Function to add ordinal suffix to day
  const getOrdinalSuffix = (day) => {
    if (day > 3 && day < 21) return "th";
    switch (day % 10) {
      case 1:
        return "st";
      case 2:
        return "nd";
      case 3:
        return "rd";
      default:
        return "th";
    }
  };

  return `${day}${getOrdinalSuffix(day)} ${month} ${year}`;
};

export const on_mobile = () => {
  return /mobile|ip(hone|od|ad)|android|blackberry|opera mini/i.test(
    navigator.userAgent,
  );
};

// Constants for date formats
const DATE_FORMATS = {
  DISPLAY: "Do MMM YYYY",
  DETAILED: "Do /MMM /YYYY",
  STANDARD: "DD/MM/YYYY",
  ISO: "YYYY-MM-DD",
};

// Base function to create moment objects with validation
const createMoment = (dateInput) => {
  const momentObj = moment(dateInput);
  if (!momentObj.isValid()) {
    throw new Error(`Invalid date: ${dateInput}`);
  }
  return momentObj;
};

// Base function for formatting dates
const formatDate = (dateInput, format) => {
  const momentObj = createMoment(dateInput);
  return momentObj.format(format);
};

// Exported functions
export const format_date = (ts) => formatDate(ts, DATE_FORMATS.DISPLAY);

export const format_dt_ago = (ts) => {
  const momentObj = createMoment(ts);
  return `${momentObj.format(DATE_FORMATS.DETAILED)} (${momentObj.fromNow()})`;
};

export const format_dt_age = (dob) => {
  const dobMoment = createMoment(dob);
  const currentMoment = moment();

  const age = currentMoment.diff(dobMoment, "years");
  return `${dobMoment.format(DATE_FORMATS.STANDARD)} (${age} years old)`;
};

export const formatDob = (dateString) => {
  const dateParts = dateString.split("/");

  if (dateParts.length !== 3) {
    throw new Error("Invalid date format. Must be in MM/DD/YYYY format.");
  }

  const [month, day, year] = dateParts;

  // Validate that all parts exist and are numeric
  if (
    !month ||
    !day ||
    !year ||
    Number.isNaN(month) ||
    Number.isNaN(day) ||
    Number.isNaN(year)
  ) {
    throw new Error("Invalid date format. Must be in MM/DD/YYYY format.");
  }

  // Pad with zeros and return ISO format
  const paddedMonth = month.padStart(2, "0");
  const paddedDay = day.padStart(2, "0");

  return `${year}-${paddedMonth}-${paddedDay}`;
};

export const getMinDate = () => {
  const currentDate = new Date();
  currentDate.setFullYear(currentDate.getFullYear() - 18); // Subtract 18 years
  return currentDate.toISOString().split("T")[0]; // Return as YYYY-MM-DD format
};

export const formattedPhoneNumber = (phoneNumber) => {
  // Ensure phoneNumber is a string before calling replace
  return phoneNumber ? phoneNumber.replace(/\s+/g, "") : "";
};

/**
/**
 * Format data by trimming strings and converting dob to yyyy-dd-mm format.
 * @param {Object} data - The data object to format.
 * @returns {Object} - The formatted data object.
 */
const formatData = (data) => {
  // Create a new object with trimmed values
  const trimmedData = Object.keys(data).reduce((acc, key) => {
    acc[key] = typeof data[key] === "string" ? data[key].trim() : data[key];
    return acc;
  }, {});

  // Return the trimmed data object without modifying dob
  return trimmedData;
};

export default formatData;

export const formatBarcode = (barcode) => {
  let cleanedBarcode = barcode.replace(/[^a-zA-Z0-9]/g, ""); // Remove non-alphanumeric characters

  let firstSegment = cleanedBarcode
    .slice(0, 3)
    .toUpperCase()
    .replace(/[^A-Z]/g, ""); // Capitalize and remove non-alphabets
  let secondSegment = cleanedBarcode
    .slice(3, 6)
    .replace(/[^a-zA-Z]/g, "")
    .toLowerCase(); // Remove non-alphabets
  let remainingSegment = cleanedBarcode.slice(6, 10);

  if (remainingSegment) {
    return `${firstSegment}-${secondSegment}-${remainingSegment}`;
  } else if (secondSegment) {
    return `${firstSegment}-${secondSegment}`;
  } else {
    return firstSegment;
  }
};

export const statusMapping = {
  registered: { variant: "primary", label: "Registered" },
  created: { variant: "primary", label: "Registered" },
  processing: { variant: "yellow", label: "Processing" },
  in_transit: { variant: "powder-pink", label: "In Transit" },
  processing_consultation: { variant: "mint", label: "Processing" },
  processing_mint: { variant: "mint", label: "Processing" },
  processing_raspberry: { variant: "raspberry", label: "Processing" },
  results_ready: { variant: "dark-green", label: "Results Ready" },
  activating: { variant: "grey-lilac", label: "Activating" },
  rejected: { variant: "raspberry", label: "Rejected" },
};

// Get icon by test name
export const iconHelper = (name) => {
  const iconList = [
    // DNA
    Glucose,
    Heart,
    Intolerances,
    Vitamins,
    Weight,

    // Blood
    Cholesterol,
    Energy_Profile,
    Male_Sexual_Health,
    General_Health_Test,
    Heart_Profile,
    Female_Sexual_Health,
    Menopause,
    PCOS,
    Thyroid,
    Erectile_Dysfunction,
    Vitamins_and_Minerals,
    Weight_Management,
    blood_vitamin_b12,
    blood_testosterone,

    // Biomarkers
    Biomarker_Cholesterol,
    Biomarker_Full_Blood_Count,
    Biomarker_Hormones,
    Biomarker_Kidney,
    Biomarker_Liver,
    Biomarker_Sexual_Health,
    Biomarker_Thyroid,
    Biomarker_Vitamins_and_Minerals,
  ];

  const testNames = [
    // DNA
    "Glucose",
    "Heart",
    "Intolerances",
    "Vitamins",
    "Weight",

    // Blood
    "Cholesterol Profile Test",
    "Energy Profile",
    "Male Sexual Health",
    "General Health",
    "Heart Profile",
    "Female Sexual Health",
    "Menopause",
    "PCOS",
    "Thyroid",
    "Erectile Dysfunction",
    "Vitamins & Minerals Profile Test",
    "Weight Management",
    "Vitamin B12",
    "Testosterone",

    // Biomarkers
    "Biomarker Cholesterol",
    "Biomarker Full Blood Count",
    "Biomarker Hormones",
    "Biomarker Kidney",
    "Biomarker Liver",
    "Biomarker Sexual Health",
    "Biomarker Thyroid",
    "Biomarker Vitamins and Minerals",
  ];

  for (let i = 0; i < iconList.length; i++) {
    if (String(name).toLowerCase().includes(testNames[i].toLowerCase())) {
      // eslint-disable-next-line no-unused-vars
      return iconList[i];
    } else if (
      name === "Male Sexual Health Test" ||
      name === "Biomarker Male Sexual Health Test"
    ) {
      return Male_Sexual_Health;
    } else if (
      name === "Female Sexual Health Test" ||
      name === "Biomarker Female Sexual Health Test"
    ) {
      return Female_Sexual_Health;
    }
  }
};

// Function to convert a string with underscores or dashes to title case
export const as_title = (s) => {
  return (s || "")
    .replace(/[ _-]/g, " ")
    .replace(/([ _-]|\b)\w/g, (l) => l.toUpperCase())
    .replace(/\bdna\b/gi, "DNA");
};

export function format_value(value, fmt, choices) {
  if (value === "No results") {
    return (value = "Enter address manually");
  }
  // Handles boolean values without fmt
  if (typeof value === "boolean" && !fmt) {
    const icon = value ? faCheck : faTimes;
    return <FontAwesomeIcon icon={icon} />;
  }

  // Returns a muted dash for falsy but defined values
  if (!value) {
    return <span className="text-muted">&mdash;</span>;
  }

  // Handles choices
  if (choices) {
    const selected = choices.find((c) => c.value === value);
    return selected ? selected.label : value;
  }

  // Checks for fmt being defined
  if (!fmt) {
    return toSentenceCase(value);
  }

  // Formats based on fmt
  switch (fmt) {
    case "datetime":
      return format_dt_ago(value);
    case "date":
      return format_date(value);
    case "inline-code":
      return <code className="inline-code">{value}</code>;
    case "as-title":
      return as_title(value);
    case "as-country":
      if (globalThis.country_lookup) {
        return (
          toSentenceCase(globalThis.country_lookup[value]) ||
          toSentenceCase(value)
        );
      }
      return toSentenceCase(value);
    default:
      return toSentenceCase(value);
  }
}

const YoutubeEmbed = ({ embedId }) => (
  <div className="video-responsive">
    <iframe
      width="853"
      height="480"
      src={`https://www.youtube.com/embed/${embedId}`}
      frameBorder="0"
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
      allowFullScreen
      title="Embedded youtube"
    />
  </div>
);

YoutubeEmbed.propTypes = {
  embedId: PropTypes.string.isRequired,
};

// Function to convert string into a URL-friendly format (slug)
const slugify = (s) => s?.toLowerCase().replace(/\s/, "-") || "";

// Function to track events with associated data
export function track(event, label) {
  if (process.env.NODE_ENV === "development") {
    return; // Don't send tracking events in development
  }

  const data = {
    event: slugify(event),
    label: slugify(label),
    page_width: window.innerWidth,
    page_height: window.innerHeight,
  };

  fetch("/track/", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
}

// Defines colors and labels for different effects
export const effect_data = {
  risk: {
    color: "nell-purple-2",
    leftLabel: "Lower Risk",
    rightLabel: "Higher Risk",
    centreLabel: "Medium Risk",
  },
  advantage: {
    color: "nell-blue",
    leftLabel: "Lower Advantage",
    rightLabel: "Higher Advantage",
    centreLabel: "Medium Advantage",
  },
  interest: {
    color: "nell-orange",
    leftLabel: "Lower Interest",
    rightLabel: "Higher Interest",
    centreLabel: "Medium Interest",
  },
};

// Function to determine intensity level
export const getIntensity = (intensity, effect) => {
  if (effect === "risk") {
    if (intensity === 0) {
      return "low-intensity";
    } else if (intensity === 1) {
      return "high-intensity";
    }

    return "medium-intensity";
  }

  if (intensity === 1) {
    return "low-intensity";
  } else if (intensity === 0) {
    return "high-intensity";
  }

  return "medium-intensity";
};

export const BankHolidayHelper = () => {
  const { callApi, resultGet } = useApis();
  const auth = useAuth();
  const [isBankHoliday, setIsBankHoliday] = useState(null);

  useEffect(() => {
    callApi(null, "/api/dashboard/bank-holiday", "GET");
  }, [localStorage.getItem("isLoggedIn")]);

  useEffect(() => {
    if (resultGet) {
      setIsBankHoliday(resultGet?.data?.is_bank_holiday);
      auth?.setIsHcpLive(resultGet?.data?.is_hcp_live);
      auth?.setIsPkbLive(resultGet?.data?.is_pkb_live);
      localStorage.setItem("isHcpLive", resultGet?.data?.is_hcp_live);
      localStorage.setItem("isPkbLive", resultGet?.data?.is_pkb_live);
    }
  }, [resultGet]);

  return isBankHoliday;
};

// Helper function to clear seconds and milliseconds
export const clearSecondsAndMilliseconds = (date) => {
  return new Date(date.setSeconds(0, 0));
};

// Converts string to sentence case
export function toSentenceCase(str) {
  if (!str) return "";

  // Helper function to check if a word is an abbreviation
  const isAbbreviation = (word) => /^[A-Z]+$/.test(word);

  // Split the string into an array of words
  const words = str.split(" ");

  const transformedWords = words.map((word, index) => {
    // If the word is inside parentheses, leave it as-is
    if (word.startsWith("(") && word.endsWith(")")) {
      return word; // Leave words like "(PCOS)" intact
    }

    // If the word is an abbreviation, leave it as-is
    if (isAbbreviation(word.replace(/[()]/g, ""))) {
      return word; // Handle abbreviations like "PCOS"
    }

    // Capitalize the first word
    if (index === 0) {
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    }

    // For other words, lowercase them
    return word.toLowerCase();
  });

  // Join the words back into a sentence
  return transformedWords.join(" ");
}

// Helper functions for handling different error types
const extractMessageFromObject = (obj) => {
  // Check for React element
  if (obj?.$$typeof) {
    return obj;
  }

  // Check for common error properties
  if (obj.message) return obj.message;
  if (obj.error) return obj.error;
  if (obj.text) return obj.text;

  // Check for any string properties
  const stringProps = Object.values(obj).filter(
    (val) => typeof val === "string",
  );
  if (stringProps.length > 0) {
    return stringProps.join(" ");
  }

  // Fallback
  return "An error occurred";
};

// Process HTML-like content
const processHtmlContent = (messageStr) => {
  try {
    // Sanitize BEFORE it ever touches innerHTML.
    // Strips <script>, event handler attrs (onerror, onload, etc.),
    // javascript: URLs, <iframe>, <object>, etc.
    const cleanHtml = DOMPurify.sanitize(messageStr, {
      ALLOWED_TAGS: ["b", "i", "em", "strong", "a", "br", "p", "span", "code", "pre"],
      ALLOWED_ATTR: ["href", "class"],
    });

    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = cleanHtml;

    return Array.from(tempDiv.childNodes).map((node) => processNode(node));
  } catch (e) {
    return messageStr;
  }
};

// Process individual DOM nodes
const processNode = (node) => {
  if (node.nodeType === Node.TEXT_NODE) {
    return node.textContent;
  }

  if (node.nodeType === Node.ELEMENT_NODE) {
    const children = Array.from(node.childNodes).map(processNode);

    if (node.tagName.toLowerCase() === "a") {
      return createLinkElement(node, children);
    }

    if (node.tagName.toLowerCase() === "br") {
      return <br />;
    }

    return <span>{children}</span>;
  }

  return null;
};

// Create link element from DOM node
const createLinkElement = (node, children) => {
  const href = node.getAttribute("href");
  const className =
    node.getAttribute("className") ||
    node.getAttribute("class") ||
    "replacement-link";
  const target =
    node.getAttribute("target") ||
    (href.startsWith("mailto:") ? "_self" : "_blank");
  const rel =
    node.getAttribute("rel") ||
    (target === "_blank" ? "noopener noreferrer" : "");

  return isSafeUrl(href) ? (
    <a href={href} className={className} target={target} rel={rel}>
      {children}
    </a>
  ) : (
    <>{children}</>
  );
};

// Create unique keys for React elements
const generateUniqueKey = (() => {
  let counter = 0;
  return (prefix) => `${prefix}-${counter++}`;
})();

// Process plain text to detect emails and URLs
const processPlainText = (messageStr) => {
  const emailRegex = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/g;
  const urlRegex = /https?:\/\/[^\s]+/g;

  const parts = messageStr.split(emailRegex);
  const emails = messageStr.match(emailRegex) || [];

  const processedParts = [];

  parts.forEach((part, index) => {
    // Process text part for URLs
    if (part) {
      processTextPartForUrls(part, urlRegex, processedParts);
    }

    // Add email if it exists for this part
    if (index < emails.length) {
      processedParts.push(
        <a
          key={generateUniqueKey("email")}
          href={`mailto:${emails[index]}`}
          className="replacement-link"
        >
          {emails[index]}
        </a>,
      );
    }
  });

  return processedParts.length > 0 ? processedParts : messageStr;
};

// Process text part to find and convert URLs to link elements
const processTextPartForUrls = (part, urlRegex, processedParts) => {
  const urlParts = part.split(urlRegex);
  const urls = part.match(urlRegex) || [];

  urlParts.forEach((urlPart, urlIndex) => {
    if (urlPart) {
      processedParts.push(urlPart);
    }

    if (urlIndex < urls.length) {
      processedParts.push(
        <a
          key={generateUniqueKey("url")}
          href={urls[urlIndex]}
          target="_blank"
          rel="noopener noreferrer"
          className="replacement-link"
        >
          {urls[urlIndex]}
        </a>,
      );
    }
  });
};

// Process string message
const processMessageString = (messageStr) => {
  // Check if the message contains HTML-like content
  if (messageStr.includes("<a") || messageStr.includes("<br")) {
    return processHtmlContent(messageStr);
  }

  return processPlainText(messageStr);
};

// Handle array type error messages
const handleArrayMessage = (message) => {
  // Handle array with single object (common error format)
  if (message.length === 1 && typeof message[0] === "object") {
    return formatErrorMessage(message[0]);
  }

  // Convert each item to string and join
  return message
    .map((item) => {
      if (typeof item === "string") return item;
      if (item && typeof item === "object") {
        return extractMessageFromObject(item);
      }
      return String(item);
    })
    .join(" ");
};

// Main function
export const formatErrorMessage = (message) => {
  // Safety check for React elements
  if (message && typeof message === "object" && message.$$typeof) {
    return message;
  }

  if (!message) return null;

  // Handle different message types
  if (Array.isArray(message)) {
    return handleArrayMessage(message);
  }

  if (typeof message === "string") {
    return processMessageString(message);
  }

  if (message && typeof message === "object") {
    return processMessageString(extractMessageFromObject(message));
  }

  // Final fallback
  return processMessageString(String(message));
};

export const hasSequentialChars = (str) => {
  const lower = str.toLowerCase();
  for (let i = 0; i < lower.length - 2; i++) {
    const a = lower.charCodeAt(i);
    const b = lower.charCodeAt(i + 1);
    const c = lower.charCodeAt(i + 2);

    // Only check for ascending sequences (a+1 === b && b+1 === c)
    if (a + 1 === b && b + 1 === c) {
      return true;
    }
  }
  return false;
};
/*
 * * Formats a tracking number into a readable format.
 * Handles both 13-character and 15-character formats.
 *
 * @param {string} value - The raw tracking number input.
 * @returns {string} - The formatted tracking number.
 */

export const formatTrackingNumber = (value) => {
  if (!value) return "";

  const raw = value.replace(/\s+/g, "").toUpperCase();

  // For 13-character input like DS213213123DA
  if (raw.length === 13) {
    const match = raw.match(/^([A-Z]{2})(\d{4})(\d{4})(\d)([A-Z]{2})$/);
    if (match) {
      return `${match[1]} ${match[2]} ${match[3]} ${match[4]}${match[5]}`;
    }
  }

  // For 15-character input
  const match15 = raw.match(/^([A-Z]{2})(\d{4})(\d{4})(\d{3})([A-Z]{2})$/);
  if (match15) {
    return `${match15[1]} ${match15[2]} ${match15[3]} ${match15[4]}${match15[5]}`;
  }

  return raw;
};

// Function to check if step data is empty
export const isStepDataEmpty = (data) => {
  if (!data || typeof data !== "object") return true;

  return Object.values(data).every(
    (value) =>
      value === undefined ||
      value === null ||
      (typeof value === "string" && value.trim() === "") ||
      (Array.isArray(value) && value.length === 0) ||
      (typeof value === "object" && Object.keys(value).length === 0),
  );
};

export const getResultCategoryInfo = (value) => {
  if (value > 150) {
    return {
      resultCategory: "High",
      referenceRange: ">150.0",
      retestInterval: "12 months",
      notes: "Pending doctor confirmation",
    };
  } else if (value >= 70 && value <= 150) {
    return {
      resultCategory: "Normal",
      referenceRange: "70.0–150.0",
      retestInterval: "1–2 years",
      notes: "Pending doctor confirmation",
    };
  } else if (value >= 37.5 && value < 70) {
    return {
      resultCategory: "Low-Normal",
      referenceRange: "37.5–70.0",
      retestInterval: "12 weeks",
      notes: "Confirmed",
    };
  } else if (value < 37.5) {
    return {
      resultCategory: "Low",
      referenceRange: "<37.5",
      retestInterval: "12 weeks",
      notes: "Confirmed",
    };
  }
  return null;
};

export const calculateAge = (dob) => {
  const birthDate = new Date(dob);
  const today = new Date();

  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  const dayDiff = today.getDate() - birthDate.getDate();

  if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
    age--;
  }

  return age;
};

export const calculateBMI = (weight, height) => {
  if (
    !weight ||
    !height ||
    height < 0.5 ||
    height > 3 ||
    weight < 20 ||
    weight > 500
  ) {
    return null;
  }
  const bmi = weight / Math.pow(height, 2);
  return Number.parseFloat(bmi.toFixed(1));
};

export const formatDateTime = (dateString) => {
  const date = new Date(dateString);

  const timeZone = "Europe/London";

  // Date → DD/MM/YYYY
  const formattedDate = date.toLocaleDateString("en-GB", { timeZone });

  // Time → hh:mm AM/PM
  const formattedTime = date.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
    timeZone,
  });

  return {
    date: formattedDate,
    time: formattedTime,
  };
};

export const trimAllWhiteSpaces = (text) => {
  return text.replace(/\s+/g, "");
};
export const fetchAddressOptions = async ({
  trimmedCode,
  callApi,
  postCoderurl,
  setAddressOptions,
  setFieldValue,
}) => {
  try {
    const response = await callApi({}, postCoderurl(trimmedCode), "GET");

    // API-level failure
    if (!response?.success) {
      throw new Error(`API error: ${response?.status}`);
    }

    const summaryLine = response?.data?.summaryline;

    // Empty or invalid response
    if (!Array.isArray(summaryLine) || summaryLine.length === 0) {
      setAddressOptions([]);
      setFieldValue("home_street_address", "");
      return;
    }

    // Map response to dropdown options
    const options = summaryLine.map((item) => ({
      label: item,
      value: item,
    }));
    setFieldValue("address_line_1", "");
    setFieldValue("address_line_2", "");
    setFieldValue("home_city", "");
    setAddressOptions(options);
    setFieldValue(
      "home_street_address",
      options.length === 1 ? options[0].value : "",
    );
  } catch (error) {
    setAddressOptions([]);
    setFieldValue("home_street_address", "");
  }
};

export const removeCountryCode = (phone) => {
  if (!phone) return "";
  if (typeof phone !== "string") {
    return phone;
  }
  if (phone.startsWith("+44")) {
    return phone.slice(3);
  }
  return phone;
};

export const parseAddress = (homeStreetAddress) => {
  if (!homeStreetAddress) {
    return {
      address_line_1: "",
      address_line_2: "",
      home_city: "",
    };
  }

  const parts = homeStreetAddress
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);

  return {
    address_line_1: parts[0] || "",
    home_city: parts[1] || "",
    address_line_2: parts[2] || "",
  };
};

export const toMatric = (value, unit) => {
  if (unit?.toLowerCase() !== "imperial") {
        throw new Error(
            `Unsupported unit: ${unit}. Only 'imperial' is supported.`
        );
  }

  return {
    value: Number((value * 2.54).toFixed(2)),
    unit: "metric",
  };
};


// --- Country calling codes lookup ---

const CALLING_CODES = [
  { code: '1', country: 'US', name: 'USA / Canada / Caribbean (NANP)' },
  { code: '7', country: 'RU', name: 'Russia / Kazakhstan' },
  { code: '20', country: 'EG', name: 'Egypt' },
  { code: '27', country: 'ZA', name: 'South Africa' },
  { code: '30', country: 'GR', name: 'Greece' },
  { code: '31', country: 'NL', name: 'Netherlands' },
  { code: '32', country: 'BE', name: 'Belgium' },
  { code: '33', country: 'FR', name: 'France' },
  { code: '34', country: 'ES', name: 'Spain' },
  { code: '36', country: 'HU', name: 'Hungary' },
  { code: '39', country: 'IT', name: 'Italy' },
  { code: '40', country: 'RO', name: 'Romania' },
  { code: '41', country: 'CH', name: 'Switzerland' },
  { code: '43', country: 'AT', name: 'Austria' },
  { code: '44', country: 'GB', name: 'United Kingdom' },
  { code: '45', country: 'DK', name: 'Denmark' },
  { code: '46', country: 'SE', name: 'Sweden' },
  { code: '47', country: 'NO', name: 'Norway' },
  { code: '48', country: 'PL', name: 'Poland' },
  { code: '49', country: 'DE', name: 'Germany' },
  { code: '51', country: 'PE', name: 'Peru' },
  { code: '52', country: 'MX', name: 'Mexico' },
  { code: '53', country: 'CU', name: 'Cuba' },
  { code: '54', country: 'AR', name: 'Argentina' },
  { code: '55', country: 'BR', name: 'Brazil' },
  { code: '56', country: 'CL', name: 'Chile' },
  { code: '57', country: 'CO', name: 'Colombia' },
  { code: '58', country: 'VE', name: 'Venezuela' },
  { code: '60', country: 'MY', name: 'Malaysia' },
  { code: '61', country: 'AU', name: 'Australia' },
  { code: '62', country: 'ID', name: 'Indonesia' },
  { code: '63', country: 'PH', name: 'Philippines' },
  { code: '64', country: 'NZ', name: 'New Zealand' },
  { code: '65', country: 'SG', name: 'Singapore' },
  { code: '66', country: 'TH', name: 'Thailand' },
  { code: '81', country: 'JP', name: 'Japan' },
  { code: '82', country: 'KR', name: 'South Korea' },
  { code: '84', country: 'VN', name: 'Vietnam' },
  { code: '86', country: 'CN', name: 'China' },
  { code: '90', country: 'TR', name: 'Turkey' },
  { code: '91', country: 'IN', name: 'India' },
  { code: '92', country: 'PK', name: 'Pakistan' },
  { code: '93', country: 'AF', name: 'Afghanistan' },
  { code: '94', country: 'LK', name: 'Sri Lanka' },
  { code: '95', country: 'MM', name: 'Myanmar' },
  { code: '98', country: 'IR', name: 'Iran' },
  { code: '211', country: 'SS', name: 'South Sudan' },
  { code: '212', country: 'MA', name: 'Morocco' },
  { code: '213', country: 'DZ', name: 'Algeria' },
  { code: '216', country: 'TN', name: 'Tunisia' },
  { code: '218', country: 'LY', name: 'Libya' },
  { code: '220', country: 'GM', name: 'Gambia' },
  { code: '221', country: 'SN', name: 'Senegal' },
  { code: '222', country: 'MR', name: 'Mauritania' },
  { code: '223', country: 'ML', name: 'Mali' },
  { code: '224', country: 'GN', name: 'Guinea' },
  { code: '225', country: 'CI', name: "Ivory Coast" },
  { code: '226', country: 'BF', name: 'Burkina Faso' },
  { code: '227', country: 'NE', name: 'Niger' },
  { code: '228', country: 'TG', name: 'Togo' },
  { code: '229', country: 'BJ', name: 'Benin' },
  { code: '230', country: 'MU', name: 'Mauritius' },
  { code: '231', country: 'LR', name: 'Liberia' },
  { code: '232', country: 'SL', name: 'Sierra Leone' },
  { code: '233', country: 'GH', name: 'Ghana' },
  { code: '234', country: 'NG', name: 'Nigeria' },
  { code: '235', country: 'TD', name: 'Chad' },
  { code: '236', country: 'CF', name: 'Central African Republic' },
  { code: '237', country: 'CM', name: 'Cameroon' },
  { code: '238', country: 'CV', name: 'Cape Verde' },
  { code: '239', country: 'ST', name: 'Sao Tome and Principe' },
  { code: '240', country: 'GQ', name: 'Equatorial Guinea' },
  { code: '241', country: 'GA', name: 'Gabon' },
  { code: '242', country: 'CG', name: 'Republic of the Congo' },
  { code: '243', country: 'CD', name: 'DR Congo' },
  { code: '244', country: 'AO', name: 'Angola' },
  { code: '245', country: 'GW', name: 'Guinea-Bissau' },
  { code: '246', country: 'IO', name: 'British Indian Ocean Territory' },
  { code: '247', country: 'AC', name: 'Ascension Island' },
  { code: '248', country: 'SC', name: 'Seychelles' },
  { code: '249', country: 'SD', name: 'Sudan' },
  { code: '250', country: 'RW', name: 'Rwanda' },
  { code: '251', country: 'ET', name: 'Ethiopia' },
  { code: '252', country: 'SO', name: 'Somalia' },
  { code: '253', country: 'DJ', name: 'Djibouti' },
  { code: '254', country: 'KE', name: 'Kenya' },
  { code: '255', country: 'TZ', name: 'Tanzania' },
  { code: '256', country: 'UG', name: 'Uganda' },
  { code: '257', country: 'BI', name: 'Burundi' },
  { code: '258', country: 'MZ', name: 'Mozambique' },
  { code: '260', country: 'ZM', name: 'Zambia' },
  { code: '261', country: 'MG', name: 'Madagascar' },
  { code: '262', country: 'RE', name: 'Reunion / Mayotte' },
  { code: '263', country: 'ZW', name: 'Zimbabwe' },
  { code: '264', country: 'NA', name: 'Namibia' },
  { code: '265', country: 'MW', name: 'Malawi' },
  { code: '266', country: 'LS', name: 'Lesotho' },
  { code: '267', country: 'BW', name: 'Botswana' },
  { code: '268', country: 'SZ', name: 'Eswatini' },
  { code: '269', country: 'KM', name: 'Comoros' },
  { code: '290', country: 'SH', name: 'Saint Helena' },
  { code: '291', country: 'ER', name: 'Eritrea' },
  { code: '297', country: 'AW', name: 'Aruba' },
  { code: '298', country: 'FO', name: 'Faroe Islands' },
  { code: '299', country: 'GL', name: 'Greenland' },
  { code: '350', country: 'GI', name: 'Gibraltar' },
  { code: '351', country: 'PT', name: 'Portugal' },
  { code: '352', country: 'LU', name: 'Luxembourg' },
  { code: '353', country: 'IE', name: 'Ireland' },
  { code: '354', country: 'IS', name: 'Iceland' },
  { code: '355', country: 'AL', name: 'Albania' },
  { code: '356', country: 'MT', name: 'Malta' },
  { code: '357', country: 'CY', name: 'Cyprus' },
  { code: '358', country: 'FI', name: 'Finland' },
  { code: '359', country: 'BG', name: 'Bulgaria' },
  { code: '370', country: 'LT', name: 'Lithuania' },
  { code: '371', country: 'LV', name: 'Latvia' },
  { code: '372', country: 'EE', name: 'Estonia' },
  { code: '373', country: 'MD', name: 'Moldova' },
  { code: '374', country: 'AM', name: 'Armenia' },
  { code: '375', country: 'BY', name: 'Belarus' },
  { code: '376', country: 'AD', name: 'Andorra' },
  { code: '377', country: 'MC', name: 'Monaco' },
  { code: '378', country: 'SM', name: 'San Marino' },
  { code: '379', country: 'VA', name: 'Vatican City' },
  { code: '380', country: 'UA', name: 'Ukraine' },
  { code: '381', country: 'RS', name: 'Serbia' },
  { code: '382', country: 'ME', name: 'Montenegro' },
  { code: '383', country: 'XK', name: 'Kosovo' },
  { code: '385', country: 'HR', name: 'Croatia' },
  { code: '386', country: 'SI', name: 'Slovenia' },
  { code: '387', country: 'BA', name: 'Bosnia and Herzegovina' },
  { code: '389', country: 'MK', name: 'North Macedonia' },
  { code: '420', country: 'CZ', name: 'Czech Republic' },
  { code: '421', country: 'SK', name: 'Slovakia' },
  { code: '423', country: 'LI', name: 'Liechtenstein' },
  { code: '500', country: 'FK', name: 'Falkland Islands' },
  { code: '501', country: 'BZ', name: 'Belize' },
  { code: '502', country: 'GT', name: 'Guatemala' },
  { code: '503', country: 'SV', name: 'El Salvador' },
  { code: '504', country: 'HN', name: 'Honduras' },
  { code: '505', country: 'NI', name: 'Nicaragua' },
  { code: '506', country: 'CR', name: 'Costa Rica' },
  { code: '507', country: 'PA', name: 'Panama' },
  { code: '508', country: 'PM', name: 'Saint Pierre and Miquelon' },
  { code: '509', country: 'HT', name: 'Haiti' },
  { code: '590', country: 'GP', name: 'Guadeloupe' },
  { code: '591', country: 'BO', name: 'Bolivia' },
  { code: '592', country: 'GY', name: 'Guyana' },
  { code: '593', country: 'EC', name: 'Ecuador' },
  { code: '594', country: 'GF', name: 'French Guiana' },
  { code: '595', country: 'PY', name: 'Paraguay' },
  { code: '596', country: 'MQ', name: 'Martinique' },
  { code: '597', country: 'SR', name: 'Suriname' },
  { code: '598', country: 'UY', name: 'Uruguay' },
  { code: '599', country: 'CW', name: 'Curacao / Netherlands Antilles' },
  { code: '670', country: 'TL', name: 'East Timor' },
  { code: '672', country: 'NF', name: 'Norfolk Island' },
  { code: '673', country: 'BN', name: 'Brunei' },
  { code: '674', country: 'NR', name: 'Nauru' },
  { code: '675', country: 'PG', name: 'Papua New Guinea' },
  { code: '676', country: 'TO', name: 'Tonga' },
  { code: '677', country: 'SB', name: 'Solomon Islands' },
  { code: '678', country: 'VU', name: 'Vanuatu' },
  { code: '679', country: 'FJ', name: 'Fiji' },
  { code: '680', country: 'PW', name: 'Palau' },
  { code: '681', country: 'WF', name: 'Wallis and Futuna' },
  { code: '682', country: 'CK', name: 'Cook Islands' },
  { code: '683', country: 'NU', name: 'Niue' },
  { code: '685', country: 'WS', name: 'Samoa' },
  { code: '686', country: 'KI', name: 'Kiribati' },
  { code: '687', country: 'NC', name: 'New Caledonia' },
  { code: '688', country: 'TV', name: 'Tuvalu' },
  { code: '689', country: 'PF', name: 'French Polynesia' },
  { code: '690', country: 'TK', name: 'Tokelau' },
  { code: '691', country: 'FM', name: 'Micronesia' },
  { code: '692', country: 'MH', name: 'Marshall Islands' },
  { code: '850', country: 'KP', name: 'North Korea' },
  { code: '852', country: 'HK', name: 'Hong Kong' },
  { code: '853', country: 'MO', name: 'Macau' },
  { code: '855', country: 'KH', name: 'Cambodia' },
  { code: '856', country: 'LA', name: 'Laos' },
  { code: '880', country: 'BD', name: 'Bangladesh' },
  { code: '886', country: 'TW', name: 'Taiwan' },
  { code: '960', country: 'MV', name: 'Maldives' },
  { code: '961', country: 'LB', name: 'Lebanon' },
  { code: '962', country: 'JO', name: 'Jordan' },
  { code: '963', country: 'SY', name: 'Syria' },
  { code: '964', country: 'IQ', name: 'Iraq' },
  { code: '965', country: 'KW', name: 'Kuwait' },
  { code: '966', country: 'SA', name: 'Saudi Arabia' },
  { code: '967', country: 'YE', name: 'Yemen' },
  { code: '968', country: 'OM', name: 'Oman' },
  { code: '970', country: 'PS', name: 'Palestine' },
  { code: '971', country: 'AE', name: 'United Arab Emirates' },
  { code: '972', country: 'IL', name: 'Israel' },
  { code: '973', country: 'BH', name: 'Bahrain' },
  { code: '974', country: 'QA', name: 'Qatar' },
  { code: '975', country: 'BT', name: 'Bhutan' },
  { code: '976', country: 'MN', name: 'Mongolia' },
  { code: '977', country: 'NP', name: 'Nepal' },
  { code: '992', country: 'TJ', name: 'Tajikistan' },
  { code: '993', country: 'TM', name: 'Turkmenistan' },
  { code: '994', country: 'AZ', name: 'Azerbaijan' },
  { code: '995', country: 'GE', name: 'Georgia' },
  { code: '996', country: 'KG', name: 'Kyrgyzstan' },
  { code: '998', country: 'UZ', name: 'Uzbekistan' },
];

const SORTED_CALLING_CODES = [...CALLING_CODES].sort((a, b) => b.code.length - a.code.length);
const MAX_CODE_LENGTH = Math.max(...CALLING_CODES.map((c) => c.code.length));

/**
 * Get the country for a phone number string that includes a "+" and calling code.
 * @param {string} phoneNumber e.g. "+919876543210"
 * @returns {{ code: string, country: string, name: string } | { error: string }}
 */
export const getCountryFromPhone=(phoneNumber)=> {
  if (!phoneNumber || typeof phoneNumber !== 'string') {
    return { error: 'Phone number must be a non-empty string' };
  }

  const cleaned = phoneNumber.replace(/[\s\-().]/g, '');

  if (!cleaned.startsWith('+')) {
    return { error: 'Phone number must start with "+" followed by the country code' };
  }

  const digits = cleaned.slice(1);

  if (!/^\d+$/.test(digits)) {
    return { error: 'Phone number contains invalid characters' };
  }

  for (let len = Math.min(MAX_CODE_LENGTH, digits.length); len >= 1; len--) {
    const prefix = digits.slice(0, len);
    const match = SORTED_CALLING_CODES.find((c) => c.code === prefix);
    if (match) {
      return { code: match.code, country: match.country, name: match.name };
    }
  }

  return { error: 'Country code not found' };
}

export const removeCountryCodeForUkNumber = (number) => {
  if (!number || typeof number !== "string") {
    return "";
  }
  const country = getCountryFromPhone(number);
  const countryCode = country?.code ? `+${country.code}` : "";
  if (countryCode && number.startsWith(countryCode)) {
    return number.slice(countryCode.length).trim();
  }

  return number.trim();
};

export const cleanDetails = (text = "") =>
  text
    .replace(/\(\s*\d+(?:\s*,\s*\d+)*\s*\)\s*/g, "")
    .replace(/\s+\./g, ".")
    .trim();